import { Clock, DollarSign, Lightbulb, Target } from 'lucide-react';
import { Card, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import type { CommandCenter } from '@/services/marketingIntegrations.service';
import { formatNumber } from './derive';

interface Props {
  cc: CommandCenter;
}

interface Hack {
  num: number;
  title: string;
  liveContext: string;
  steps: string[];
  tools: string[];
  impact: string;
  speed: string;
  cac: string;
}

function buildHacks(cc: CommandCenter): Hack[] {
  const pages = cc.contentPages?.rows ?? [];
  const queries = cc.gsc?.topQueries ?? [];
  const dashboard = cc.dashboard;

  // 1. Highest-traffic zero-conversion page
  const broken = [...pages].filter((p) => p.sessions >= 500 && p.conversions === 0).sort((a, b) => b.sessions - a.sessions)[0];
  // 2. Highest-engagement no-CTA
  const gem = [...pages].filter((p) => p.engagementRate >= 80 && p.conversions === 0 && p.sessions >= 100).sort((a, b) => b.engagementRate - a.engagementRate)[0];
  // 3. Highest-impression zero-click query
  const zeroClick = [...queries].filter((q) => q.clicks === 0 && q.impressions >= 100).sort((a, b) => b.impressions - a.impressions)[0];
  // 4. CTV retargeting — total site sessions
  const totalSessions = pages.reduce((s, p) => s + p.sessions, 0);
  // 5. Job/life-event audience: pricing page traffic
  const pricingSessions = pages.filter((p) => p.category === 'pricing').reduce((s, p) => s + p.sessions, 0);
  // 6. Competitor conquest: comparison-intent queries
  const compQueries = queries.filter((q) => /\b(vs\.?|versus|alternative|compare)\b/i.test(q.query));
  const compSessions = compQueries.reduce((s, q) => s + q.impressions, 0);
  // 7. B2B distribution: top organic landing
  const topOrganicLanding = [...pages].filter((p) => p.category !== 'other').sort((a, b) => b.sessions - a.sessions)[0];
  // 8. AI lifecycle email: GA4 conv count this period
  const totalConv = (dashboard.google_analytics?.conversions ?? 0);
  // 9. YouTube/podcast: educational query volume
  const eduImpressions = queries.filter((q) => /\b(how|what|guide|tutorial)\b/i.test(q.query)).reduce((s, q) => s + q.impressions, 0);
  // 10. Direct mail HNW: top-performing product page
  const topProduct = [...pages].filter((p) => p.category === 'product' || p.category === 'pricing').sort((a, b) => b.sessions - a.sessions)[0];

  const hacks: Hack[] = [
    {
      num: 1,
      title: 'Fix the highest-traffic zero-conversion page',
      liveContext: broken
        ? `${broken.path} attracted ${formatNumber(broken.sessions)} sessions and 0 conversions in 30 days. At 2.5% baseline that's ~${Math.round(broken.sessions * 0.025)} leads/month leaving the table.`
        : 'No page has yet hit 500+ sessions with zero conversions — connect GA4 to surface this opportunity.',
      steps: [
        'Audit the page in person — what does the visitor expect, what do they actually see?',
        'Add a single primary CTA above the fold matching the page intent.',
        'Wire a GA4 conversion event to the new CTA so you can measure lift.',
        'Drop a Meta + Google retargeting pixel so non-converting traffic still gets a second chance.',
        'Re-test in 14 days; if conversions are still <1%, rewrite the H1 to match the inbound query.',
      ],
      tools: ['GA4', 'Tag Manager', 'Meta Pixel', 'Hotjar (recording)'],
      impact: broken ? `~${Math.round(broken.sessions * 0.025)} leads / mo` : '~25 leads / mo / fixed page',
      speed: '1 sprint (1 week)',
      cac: '$0 — fixing existing traffic',
    },
    {
      num: 2,
      title: 'Capture leads from high-engagement content',
      liveContext: gem
        ? `${gem.path} has ${gem.engagementRate.toFixed(0)}% engagement on ${formatNumber(gem.sessions)} sessions but no email-capture event tagged. These readers are the most pre-qualified audience you have.`
        : 'No "hidden gem" surfaced yet (engagement ≥80% with no conversions). Connect GA4.',
      steps: [
        'Add a scroll-triggered email capture (60% scroll) with a content-relevant magnet.',
        'Drop an exit-intent variant that offers the same magnet on a second-chance.',
        'Tag both as GA4 conversion events.',
        'Build a Meta + Google audience of these page visitors and run a retargeting campaign.',
        'Sequence the magnet recipients into a 5-touch nurture email automation.',
      ],
      tools: ['ConvertKit / Mailchimp', 'Beacon / Tally (capture)', 'GA4', 'Meta + Google audiences'],
      impact: '+15-30 emails / mo / page',
      speed: '3 days',
      cac: '$0 — already-engaged traffic',
    },
    {
      num: 3,
      title: 'Win the highest-impression zero-click SEO query',
      liveContext: zeroClick
        ? `"${zeroClick.query}" gets ${formatNumber(zeroClick.impressions)} impressions / mo at #${zeroClick.position.toFixed(0)} but 0 clicks. Title-tag rewrite + snippet target.`
        : 'No high-impression zero-click query yet. Connect Search Console.',
      steps: [
        'Pull the SERP for the query — see what is owned (PAA, AI Overview, featured snippet).',
        'Rewrite the page title to match a question or comparison framing.',
        'Add a 50-word direct-answer block at the top of the page (snippet bait).',
        'Add proper FAQ schema (JSON-LD) to claim People Also Ask slots.',
        'Re-monitor weekly in Search Console; expect 3-6 weeks for Google to re-rank.',
      ],
      tools: ['Search Console', 'Schema.org Markup Validator', 'AnswerThePublic (snippet ideas)'],
      impact: zeroClick ? `~${Math.round(zeroClick.impressions * 0.05)} clicks / mo at 5% CTR` : '~50-200 clicks / mo',
      speed: '1 week',
      cac: '$0 — pure organic',
    },
    {
      num: 4,
      title: 'CTV retargeting of site visitors',
      liveContext: `Your site receives ${formatNumber(totalSessions)} sessions / mo. CTV ads to that audience are usually 60-70% cheaper CPM than display and reach the household — not just the device.`,
      steps: [
        'Build a Meta + Google + YouTube custom audience of all site visitors (last 90 days).',
        'Produce or repurpose a 15-second video ad — value prop + clear CTA.',
        'Launch on YouTube + Meta video placements at $50/day cap.',
        'Layer in a "deep engagement" segment (visitors who hit pricing page).',
        'Measure assisted conversions in GA4 — direct CTV ROAS reads slow but lifts other channels.',
      ],
      tools: ['YouTube Ads', 'Meta Ads', 'GA4 (assisted conversions)', 'Canva or Adobe Express (video)'],
      impact: '+10-20% lift in branded search and direct traffic',
      speed: '2 weeks',
      cac: '~$30-60 blended CAC',
    },
    {
      num: 5,
      title: 'Job-change / life-event intent targeting',
      liveContext: `${formatNumber(pricingSessions)} sessions reach pricing pages — these are the closest-to-buy intent signals. Use LinkedIn job-change events + Meta major-life-event audiences to find more of them upstream.`,
      steps: [
        'In LinkedIn, build a "Job Change in last 90 days" + your ICP industry/title audience.',
        'Run a single $30/day prospecting campaign with a pricing-page-equivalent landing page.',
        'On Meta, layer a major-life-event audience (new home, new job) onto your demographics.',
        'Use offline conversions (Pipedrive/CRM → LinkedIn + Meta) for attribution beyond clicks.',
        'Cap test at 30 days; if CPL ≤ 2× your blended target, scale.',
      ],
      tools: ['LinkedIn Ads', 'Meta Ads', 'Pipedrive / HubSpot offline conversions'],
      impact: '~10-20 leads / mo at higher LTV',
      speed: '2 weeks',
      cac: '$60-150 (premium audience)',
    },
    {
      num: 6,
      title: 'Competitor conquesting',
      liveContext: compQueries.length > 0
        ? `${compQueries.length} comparison/alternative queries are already showing impressions (${formatNumber(compSessions)} total). These are decision-stage searchers — running paid alongside the SEO play maximizes capture.`
        : 'No comparison-intent SEO queries surfaced yet — start by adding a comparison page to your site.',
      steps: [
        'Build a head-to-head comparison page for your top 3 competitors.',
        'Target competitor brand keywords with exact-match Google Search ads pointing at the comparison page.',
        'Cap CPC bid at 2× your blended CPC, copy mentions price/value differentiator.',
        'Use Google Ads "competitors" audience signals on Performance Max if running it.',
        'Add Meta lookalike of "people who visited comparison page" as a prospecting layer.',
      ],
      tools: ['Google Ads', 'Meta Ads', 'Search Console (impression share)'],
      impact: '~20-40 high-intent leads / mo',
      speed: '2-3 weeks (page first)',
      cac: '$80-180 — premium SERP',
    },
    {
      num: 7,
      title: 'B2B distribution channel — newsletter sponsorships',
      liveContext: topOrganicLanding
        ? `${topOrganicLanding.path} is your strongest content asset (${formatNumber(topOrganicLanding.sessions)} sessions). Repurpose it into a newsletter-sponsorship landing page for a tier-1 industry newsletter.`
        : 'Once GA4 reports your top organic landing page, this hack identifies a sponsor target.',
      steps: [
        'List 5 newsletters with 10K–50K B2B subscribers in your space (Sparkloop, Letterbase, Swapstack).',
        'Negotiate a 1-month, 4-issue sponsorship at $1.5–4K total.',
        'Drive sponsored traffic to a dedicated UTM-tagged landing page mirroring your top organic content.',
        'A/B test sponsorship copy: question-headline vs claim-headline.',
        'After 1 month, evaluate CPL. If under 2× target, expand to 2-3 newsletters concurrently.',
      ],
      tools: ['Sparkloop', 'Swapstack', 'UTM builder', 'GA4'],
      impact: '~50-150 high-quality leads / sponsorship cycle',
      speed: '3-4 weeks',
      cac: '$30-90 (newsletter-specific)',
    },
    {
      num: 8,
      title: 'AI-powered lifecycle email',
      liveContext: `You are tracking ~${formatNumber(totalConv)} conversions / mo. Even a basic 6-email lifecycle sequence with AI-personalized subject lines typically improves open rate by 30-40% over generic.`,
      steps: [
        'Map your 6 lifecycle stages: signup → onboarding → first-value → repeat-engagement → upsell → win-back.',
        'Write a generic email at each stage. Use Claude or GPT-4 to generate 3 subject-line variants per stage.',
        'Run subject-line variants through Mailchimp/Klaviyo A/B testing — 50/50 send.',
        'Personalize body copy based on user attributes pulled from your CRM (industry, role).',
        'Re-evaluate quarterly: rotate top performer into "control" and test new variants.',
      ],
      tools: ['Klaviyo / Mailchimp / Customer.io', 'Claude / GPT-4 API', 'Your CRM'],
      impact: '+15-25% conversion lift across funnel',
      speed: '4 weeks',
      cac: '$0 — owned audience',
    },
    {
      num: 9,
      title: 'YouTube / podcast organic compounding',
      liveContext: eduImpressions > 0
        ? `${formatNumber(eduImpressions)} impressions on educational queries this month. Same intent works as YouTube content — pickup rates compound for 18+ months.`
        : 'Educational queries have not yet appeared in Search Console. This hack works after you have published 5–10 educational pages.',
      steps: [
        'Pull your top 10 educational queries from Search Console.',
        'Record one 5–10 minute YouTube video per query, optimized title with the exact query phrase.',
        'Embed the video into the corresponding article on your site.',
        'Cross-post to LinkedIn (3-min cut) + Twitter (clip).',
        'Maintain a 1-video / week cadence — first 12 videos build the catalog, weeks 13+ start compounding.',
      ],
      tools: ['Riverside / Descript', 'YouTube Studio', 'Descript clip generation'],
      impact: '~5K-50K monthly views by month 12',
      speed: '12 weeks to compound',
      cac: 'Time-only: ~6 hrs / week',
    },
    {
      num: 10,
      title: 'Direct mail to HNW households',
      liveContext: topProduct
        ? `${topProduct.path} is your top product/pricing destination (${formatNumber(topProduct.sessions)} sessions). Send a high-tier direct-mail piece to HNW prospects with a unique offer code that drops them on this page.`
        : 'Once a flagship product/pricing page is identified, direct mail provides an extremely low-noise channel to HNW prospects.',
      steps: [
        'Buy a list from Acxiom or Experian filtered by income band, ZIP, household demographics.',
        'Design a single-sheet mailer — premium print, unique offer code, 1 clear CTA + URL.',
        'Mail in batches of 1,000 to test creative variants.',
        'Trackable: each mailer has a unique URL parameter or vanity domain.',
        'Measure CAC over 60 days — direct mail typically converts at 2-5% on premium audiences.',
      ],
      tools: ['Lob (mail-as-a-service)', 'Acxiom / Experian list', 'Custom landing page'],
      impact: '20-50 conversions / mailing cycle',
      speed: '4 weeks',
      cac: '$200-400 (premium prospects)',
    },
  ];

  return hacks;
}

export default function GrowthHacksTab({ cc }: Props) {
  const hacks = buildHacks(cc);

  return (
    <div className="p-6 space-y-4">
      <div className="rounded-lg border border-violet-200 bg-violet-50 px-4 py-3 text-sm text-violet-900 flex items-center gap-3">
        <Lightbulb className="h-4 w-4 flex-shrink-0" />
        <span>10 hacks below. Each opens with a live data point — execute the ones whose context resonates with your numbers.</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {hacks.map((h) => (
          <Card key={h.num}>
            <CardBody>
              <div className="flex items-start gap-3 mb-3">
                <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-violet-100 text-violet-700 grid place-items-center font-bold">{h.num}</div>
                <div className="flex-1">
                  <div className="text-base font-semibold text-slate-900">{h.title}</div>
                </div>
              </div>

              <div className="rounded-md border-l-4 border-violet-400 bg-violet-50 px-3 py-2 mb-3">
                <div className="text-[11px] uppercase tracking-wide text-violet-700 font-bold mb-1">Live data context</div>
                <div className="text-sm text-slate-700 leading-relaxed">{h.liveContext}</div>
              </div>

              <ol className="space-y-1.5 text-sm text-slate-700 mb-4">
                {h.steps.map((s, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-slate-400 font-mono text-xs flex-shrink-0 w-5 text-right">{i + 1}.</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ol>

              <div className="flex flex-wrap gap-1.5 mb-3">
                {h.tools.map((t) => <Badge key={t} variant="neutral" size="sm">{t}</Badge>)}
              </div>

              <div className="grid grid-cols-3 gap-2 text-xs pt-3 border-t border-slate-100">
                <div>
                  <div className="text-slate-500 flex items-center gap-1"><Target className="h-3 w-3" /> Impact</div>
                  <div className="font-semibold text-slate-900 mt-0.5">{h.impact}</div>
                </div>
                <div>
                  <div className="text-slate-500 flex items-center gap-1"><Clock className="h-3 w-3" /> Speed</div>
                  <div className="font-semibold text-slate-900 mt-0.5">{h.speed}</div>
                </div>
                <div>
                  <div className="text-slate-500 flex items-center gap-1"><DollarSign className="h-3 w-3" /> CAC</div>
                  <div className="font-semibold text-slate-900 mt-0.5">{h.cac}</div>
                </div>
              </div>
            </CardBody>
          </Card>
        ))}
      </div>
    </div>
  );
}
